/**
 * PajesTab — Phase 6a.16 per Frontend Spec §21.11.
 *
 * The 6th and final results tab. Surfaces
 * `engagement.lastResult.aggregations.pajes` (Phase 6c.1a sealed) — the
 * backend's auditor-proposed journal entry list — as a stack of
 * expandable cards. Read-only in this phase; the "confirmed PAJE → JE"
 * workflow is post-MVP (Phase 6b).
 *
 * Composition:
 *   • Header: title + count badge ({filtered}/{total} or single) + total
 *     variance summed Decimal-safe across ALL pajes (not filtered).
 *   • Toolbar: single Select for the 3-type filter; Clear-Filters ghost
 *     button when active.
 *   • Cards: each PajeCard owns its expand state; collapsed by default;
 *     contractId Link in the body navigates via Phase 6a.18b helper.
 *   • Two empty states: "no PAJEs" (educational hint) and "filtered
 *     to zero" (relax filters).
 *
 * Architectural constraints from the prompt:
 *   • engagement access via useOutletContext (matches Yevmiye)
 *   • Decimal-safe variance summation (currency strings, no Number drift)
 *   • Dr/Cr balance check reuses the extracted JELinesTable primitive
 *   • contractId Link uses buildContractDetailPath (no `?tab=` for summary)
 */

import {
  ChevronDown,
  ChevronRight,
  FileQuestion,
  Info,
  X,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useOutletContext } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ExplainerPanel } from "@/components/common/ExplainerPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JELinesTable } from "@/features/engagement/components/JELinesTable";
import { PajeConsolidatedSummary } from "@/features/engagement/components/PajeConsolidatedSummary";
import { PostingGuidancePanel } from "@/features/engagement/components/PostingGuidancePanel";
import { PajeTypeBadge } from "@/features/engagement/components/PajeTypeBadge";
import { sumDecimalStrings as sumDecimalStringsImpl } from "@/features/export/helpers/sumDecimalStrings";
import { buildPajeStory } from "@/lib/buildPajeStory";
import { formatCurrency, formatSigned } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  translatePajeDescription,
  type TFn as PajeTranslateTFn,
} from "@/lib/translatePajeDescription";
import { buildContractDetailPath } from "@/pages/engagement/utils/contractDetailLink";

// Phase 6a.20 — Re-export the Decimal-safe helper extracted in Phase 6a.16
// so the existing PajesTab tests + the new Excel export pipeline both
// resolve `sumDecimalStrings` from a single implementation source.
export const sumDecimalStrings = sumDecimalStringsImpl;
import type {
  AggregatedPaje,
  AggregatedPajeType,
  EngagementHistoryEntry,
} from "@/types";

interface OutletContext {
  engagement: EngagementHistoryEntry;
}

type TypeFilter = AggregatedPajeType | "ALL";

export function PajesTab() {
  const { engagement } = useOutletContext<OutletContext>();
  const pajes = engagement.lastResult?.aggregations.pajes ?? [];
  const currency = engagement.functionalCurrency;

  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>("ALL");

  const filtered = React.useMemo(() => {
    if (typeFilter === "ALL") return pajes;
    return pajes.filter((p) => p.type === typeFilter);
  }, [pajes, typeFilter]);

  // Phase 6a.16 §21.11.8 — Decimal-safe variance sum across ALL pajes
  // (engagement-level audit metric — NOT filtered). Variance is a
  // 2-decimal currency string from the backend; summing as integer
  // cents avoids any floating-point drift at sub-cent precision.
  const totalVariance = React.useMemo(
    () => sumDecimalStrings(pajes.map((p) => p.variance)),
    [pajes],
  );

  if (pajes.length === 0) {
    return <PajeEmptyState />;
  }

  return (
    <div className="space-y-4" data-testid="pajes-tab">
      {/* MINI_FIX_BR Part 2 — collapsed-by-default reference accordion above
          the header. Educational surface for junior auditors / SMMM
          candidates — explains what PAJEs are, what +/− means after the
          sign fix (Part 1), and the three PAJE types. All copy via i18n
          (`paje.guide.*`). Empty-state view keeps its own existing hint. */}
      <PajeGuide />
      {/* "Yevmiye mi, PAJE mi?" — same shared explainer as the Yevmiye tab, so
          the auditor knows these are adjustments to the client's existing
          books (not the full ledger) and that the two are not posted together. */}
      <PostingGuidancePanel id="paje-posting" />
      <PajeHeader
        totalCount={pajes.length}
        filteredCount={filtered.length}
        totalVariance={totalVariance}
        currency={currency}
      />
      <PajeToolbar
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        onClearFilters={() => setTypeFilter("ALL")}
      />
      {/* MINI_FIX_BU — Konsolide PAJE Özeti panel.
          Always reflects the FULL pajes array (not `filtered`) so the
          forest-view totals don't change when the auditor flips the
          toolbar type filter for the card list below. */}
      <PajeConsolidatedSummary pajes={pajes} currency={currency} />
      {filtered.length === 0 ? (
        <PajeFilteredEmptyState />
      ) : (
        <div className="space-y-3" data-testid="paje-list">
          {filtered.map((paje) => (
            <PajeCard
              key={paje.pajeNumber}
              paje={paje}
              engagementId={engagement.id}
              currency={currency}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PajeHeader ────────────────────────────────────────────────────────────

interface PajeHeaderProps {
  totalCount: number;
  filteredCount: number;
  totalVariance: string;
  currency: string;
}

// ─── PajeGuide ─────────────────────────────────────────────────────────────

/**
 * MINI_FIX_BR Part 2 — collapsed-by-default educational accordion above
 * the PAJE list. Reuses the shadcn `Accordion` primitive (single-item,
 * collapsible). All copy is `t("paje.guide.*")`. Auditors who know PAJEs
 * never have to expand it; juniors and SMMM candidates get a quick
 * reference for the sign convention introduced in Part 1.
 */
function PajeGuide() {
  const { t } = useTranslation();
  // SPEC-08 §4.6 — extracted into the reusable `<ExplainerPanel/>`
  // primitive. The `id="paje"` keeps the same test-ids (`paje-guide` /
  // `paje-guide-trigger`) so existing tests continue to pass. Content
  // (locale keys) is unchanged.
  return (
    <ExplainerPanel id="paje" title={t("paje.guide.title")}>
      <section className="space-y-1">
        <p className="font-medium text-foreground">
          {t("paje.guide.whatIsTitle")}
        </p>
        <p className="text-muted-foreground">
          {t("paje.guide.whatIsBody")}
        </p>
      </section>

      <section className="space-y-1">
        <p className="font-medium text-foreground">
          {t("paje.guide.whatForTitle")}
        </p>
        <p className="text-muted-foreground">
          {t("paje.guide.whatForBody")}
        </p>
      </section>

      <section className="space-y-2">
        <p className="font-medium text-foreground">
          {t("paje.guide.signTitle")}
        </p>
        <p className="text-muted-foreground">
          {t("paje.guide.signBody")}
        </p>
        <ul className="space-y-1.5">
          <li className="flex items-start gap-2">
            <span
              className="mt-0.5 font-mono font-semibold text-green-400"
              aria-hidden="true"
            >
              +
            </span>
            <span className="text-muted-foreground">
              {t("paje.guide.signPositive")}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span
              className="mt-0.5 font-mono font-semibold text-destructive"
              aria-hidden="true"
            >
              −
            </span>
            <span className="text-muted-foreground">
              {t("paje.guide.signNegative")}
            </span>
          </li>
        </ul>
        <p className="text-xs text-muted-foreground italic">
          {t("paje.guide.signTotalNote")}
        </p>
      </section>

      <section className="space-y-1">
        <p className="font-medium text-foreground">
          {t("paje.guide.typesTitle")}
        </p>
        <ul className="space-y-1.5 text-muted-foreground">
          <li>{t("paje.guide.typeAdjustment")}</li>
          <li>{t("paje.guide.typeReclassification")}</li>
          <li>{t("paje.guide.typeFxRevaluation")}</li>
        </ul>
      </section>

      <p className="text-xs text-muted-foreground italic">
        {t("paje.guide.disclaimer")}
      </p>
    </ExplainerPanel>
  );
}

// ─── PajeHeader ───────────────────────────────────────────────────────────

function PajeHeader({
  totalCount,
  filteredCount,
  totalVariance,
  currency,
}: PajeHeaderProps) {
  const { t } = useTranslation();
  const countLabel =
    filteredCount === totalCount
      ? t("paje.page.count", { count: totalCount })
      : t("paje.page.filteredCount", {
          filtered: filteredCount,
          total: totalCount,
        });
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3"
      data-testid="paje-header"
    >
      <div>
        <h2 className="text-xl font-semibold">{t("paje.page.title")}</h2>
        <p
          className="text-sm text-muted-foreground"
          data-testid="paje-count"
        >
          {countLabel}
        </p>
      </div>
      <div
        className="text-sm tabular-nums"
        data-testid="paje-total-variance"
      >
        <span className="text-muted-foreground">
          {t("paje.page.totalVariance")}:
        </span>{" "}
        <span className="font-mono font-semibold">
          {formatSigned(totalVariance, currency)}
        </span>
      </div>
    </div>
  );
}

// ─── PajeToolbar ───────────────────────────────────────────────────────────

interface PajeToolbarProps {
  typeFilter: TypeFilter;
  onTypeFilterChange: (v: TypeFilter) => void;
  onClearFilters: () => void;
}

function PajeToolbar({
  typeFilter,
  onTypeFilterChange,
  onClearFilters,
}: PajeToolbarProps) {
  const { t } = useTranslation();
  const isFiltered = typeFilter !== "ALL";
  return (
    <Card className="p-4" data-testid="paje-toolbar">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="block text-xs font-medium mb-1 text-muted-foreground">
            {t("paje.toolbar.type.label")}
          </Label>
          <Select
            value={typeFilter}
            onValueChange={(v) => onTypeFilterChange(v as TypeFilter)}
          >
            <SelectTrigger
              className="w-[220px]"
              data-testid="paje-filter-type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                {t("paje.toolbar.type.all")}
              </SelectItem>
              <SelectItem value="RECLASSIFICATION">
                {t("paje.toolbar.type.reclassification")}
              </SelectItem>
              <SelectItem value="ADJUSTMENT">
                {t("paje.toolbar.type.adjustment")}
              </SelectItem>
              <SelectItem value="FX_REVALUATION">
                {t("paje.toolbar.type.fxRevaluation")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={onClearFilters}
            data-testid="paje-clear-filters"
          >
            <X className="mr-1 h-4 w-4" />
            {t("paje.toolbar.clearFilters")}
          </Button>
        )}
      </div>
    </Card>
  );
}

// ─── PajeCard ──────────────────────────────────────────────────────────────

interface PajeCardProps {
  paje: AggregatedPaje;
  engagementId: string;
  currency: string;
}

function PajeCard({ paje, engagementId, currency }: PajeCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const padded = String(paje.pajeNumber).padStart(3, "0");
  // Phase 6a.18b helper — summary tab is the default landing, so no
  // `?tab=` segment in the URL (clean shareable link).
  const detailPath = buildContractDetailPath(engagementId, paje.contractId);
  return (
    <Card
      className="overflow-hidden"
      data-testid={`paje-card-${padded}`}
    >
      {/* HEADER (toggle button) — kept link-free per Phase 6a.18b inventory
          item #1: nested interactive elements (Link inside button) are an
          HTML5 anti-pattern. The contractId Link lives in the body. */}
      <button
        type="button"
        className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-accent/30 transition-colors"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid={`paje-card-${padded}-toggle`}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="font-mono font-medium text-sm shrink-0">
          PAJE-{padded}
        </span>
        <PajeTypeBadge type={paje.type} />
        <span
          className="flex-1 text-sm text-muted-foreground truncate"
          data-testid={`paje-card-${padded}-description`}
        >
          {translatePajeDescription(
            paje.description,
            t as unknown as PajeTranslateTFn,
          )}
        </span>
        <span
          className="text-sm font-mono tabular-nums font-medium shrink-0"
          data-testid={`paje-card-${padded}-variance`}
        >
          {formatSigned(paje.variance, currency)}
        </span>
      </button>

      {open && (
        <div
          className="border-t px-4 py-3 space-y-3"
          data-testid={`paje-card-${padded}-content`}
        >
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t("paje.card.contractLabel")}:
            </span>
            <Link
              to={detailPath}
              className="font-medium underline hover:text-foreground"
              data-testid={`paje-card-${padded}-contract-link`}
            >
              {paje.contractId}
            </Link>
          </div>
          {/* MINI_FIX_BS — per-PAJE "Why?" sub-accordion. Independent from the
              card-open state: the card body shows the trigger; the story body
              only appears when the auditor opens this nested accordion.
              Mirrors the page-level guide accordion (MINI_FIX_BR Part 2) for
              visual + interaction consistency. */}
          <PajeStorySection paje={paje} currency={currency} padded={padded} />
          <JELinesTable
            lines={paje.suggestedJE}
            currency={currency}
            testIdPrefix={`paje-card-${padded}`}
          />
        </div>
      )}
    </Card>
  );
}

// ─── PajeStorySection ─────────────────────────────────────────────────────

/**
 * MINI_FIX_BS — per-PAJE "Bu PAJE neden önerildi?" sub-accordion.
 * Default collapsed, independent of the parent card's open state.
 * Story content is structured by `buildPajeStory` (pure helper); this
 * component only formats currency amounts + applies direction colours.
 */
function PajeStorySection({
  paje,
  currency,
  padded,
}: {
  paje: AggregatedPaje;
  currency: string;
  padded: string;
}) {
  const { t } = useTranslation();
  // Cast once so `t(story.whyKey, params)` works with a dynamic key
  // (react-i18next's strongly-typed overloads otherwise reject any
  // params object on a `string`-typed key).
  const tDyn = t as unknown as PajeTranslateTFn;
  const story = buildPajeStory(paje, tDyn);
  // Pre-format the amount once and pass into both why + meaning params so
  // the helper can stay format-free and locale-agnostic.
  const formattedAmount = formatCurrency(story.absVariance, currency);
  const whyParams = { ...story.whyParams, amount: formattedAmount };
  const meaningParams = { ...story.meaningParams, amount: formattedAmount };
  const directionKey =
    story.direction === "decrease"
      ? "paje.story.directionDecrease"
      : "paje.story.directionIncrease";
  const directionMarkerClass =
    story.direction === "decrease"
      ? "text-destructive"
      : "text-green-400";
  const directionMarker = story.direction === "decrease" ? "−" : "+";
  return (
    <Accordion
      type="single"
      collapsible
      className="rounded-md border bg-muted/30"
      data-testid={`paje-card-${padded}-story`}
    >
      <AccordionItem value="story" className="border-0">
        <AccordionTrigger
          className="px-3 py-2 text-sm hover:no-underline"
          data-testid={`paje-card-${padded}-story-trigger`}
        >
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <Info className="h-4 w-4" aria-hidden="true" />
            {t("paje.story.heading")}
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3 text-sm">
          <div className="space-y-2">
            <p className="text-muted-foreground">
              {tDyn(story.whyKey, whyParams)}
            </p>
            <p className="text-muted-foreground">
              {tDyn(story.meaningKey, meaningParams)}
            </p>
            <p className="flex items-start gap-2">
              <span
                className={cn(
                  "mt-0.5 font-mono font-semibold",
                  directionMarkerClass,
                )}
                aria-hidden="true"
              >
                {directionMarker}
              </span>
              <span className="text-muted-foreground">
                {tDyn(directionKey)}
              </span>
            </p>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

// ─── Empty states ──────────────────────────────────────────────────────────

function PajeEmptyState() {
  const { t } = useTranslation();
  return (
    <div
      className="text-center py-16 space-y-3"
      data-testid="paje-empty-state"
    >
      <FileQuestion
        className="h-12 w-12 mx-auto text-muted-foreground"
        aria-hidden="true"
      />
      <p className="text-muted-foreground font-medium">
        {t("paje.emptyState.title")}
      </p>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        {t("paje.emptyState.hint")}
      </p>
    </div>
  );
}

function PajeFilteredEmptyState() {
  const { t } = useTranslation();
  return (
    <p
      className="text-center italic text-muted-foreground py-8"
      data-testid="paje-filtered-empty-state"
    >
      {t("paje.emptyState.filtered")}
    </p>
  );
}

// ─── Decimal-safe helpers ──────────────────────────────────────────────────

// `sumDecimalStrings` is exported above as a re-export from the shared
// `@/features/export/helpers/sumDecimalStrings` module (single source
// of truth shared with the Excel export pipeline).
